import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useCurrentSite } from '@hotjar/site';
import type { Site } from '@hotjar/site';
import type { SiteMetadata } from '@hotjar/webapp/domains/activation/site-metadata/model';
import { getSiteMetadata } from '@hotjar/webapp/domains/activation/site-metadata/api';

const MAX_RETRIES = 10;

export const SITE_METADATA = 'siteMetadata';

export const useSiteMetadata = (
  shouldFetchSiteMetadata = false,
  waitForSiteDetection = true,
): SiteMetadata | undefined => {
  const { data: currentSite } = useCurrentSite();
  const [retries, setRetries] = useState(0);
  const [detectionFinished, setDetectionFinished] = useState(false);
  const siteId = currentSite?.id;
  const fallbackMetadata = { finished: detectionFinished };

  useEffect(() => {
    if (retries >= MAX_RETRIES) {
      setDetectionFinished(true);
    }
  }, [retries, setDetectionFinished]);

  const { data: siteMetadata, isIdle } = useQuery(
    [SITE_METADATA, { siteId }],
    () => getSiteMetadata(currentSite as Site),
    {
      enabled:
        shouldFetchSiteMetadata &&
        (waitForSiteDetection ? !!currentSite?.url : true) &&
        retries < MAX_RETRIES &&
        !detectionFinished,
      retry: false,
      refetchInterval: 300,
      refetchIntervalInBackground: true,
      onSuccess: ({ finished }) => {
        if (finished || !waitForSiteDetection) setDetectionFinished(true);
        setRetries(retries + 1);
      },
      onError: () => {
        setDetectionFinished(true);
      },
      select: ({ finished, ...data }) => {
        return { ...data, finished: finished || detectionFinished };
      },
    },
  );

  if (isIdle) return undefined;

  return siteMetadata || fallbackMetadata;
};
